import React from 'react';
import {
  makeStyles,
  Button,
  TextField,
  InputAdornment,
  LinearProgress,
} from '~/lib/bn-material-ui';
import { useStateValue } from './data/order-calculator-context';
import { BnToggleSwitch } from '../common/form-fields/bn-toggle-switch';
import _ from 'lodash';
import { useFeedOptions } from '~/feeds/feed-helpers';
import { BnAutocompleteField } from '~/common';

const useStyles = makeStyles((theme) => {
  return {
    form: {
      flex: 1,
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: theme.spacing(2),
    },
    readOnlyTextField: {
      '& .MuiOutlinedInput-root': {
        background: theme.palette.gray[20],
        color: theme.palette.grey.darker,
      },
    },
    amountContainer: {
      display: 'grid',
      gridTemplateColumns: '10em 1fr',
      gap: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr',
      },
    },
    legend: {
      display: 'inline-block',
      height: '1em',
      width: '1em',
      backgroundColor: theme.palette.lavender[60],
      opacity: 0.5,
      marginLeft: '1ch',
      borderRadius: '0.25ch',
      border: `1px solid ${theme.palette.gray[80]}`,
      marginBottom: '-2px',
    },
    maxRecommendedContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      [theme.breakpoints.down('md')]: {
        justifyContent: 'center',
      },
    },
    setMaxButton: {
      fontWeight: 'bold',
      margin: '0.25rem',
      whiteSpace: 'nowrap',
    },
    selectFeedWarning: {
      textDecoration: 'none',
      textTransform: 'none',
    },
    selectFeedText: {
      textDecoration: 'underline',
      textTransform: 'uppercase',
    },
  };
});

export default function OrderForm({ bin, maxBinLevel, orderQuantity, orderUnit, defaultFeedId, onFeedChange, setOrderQuantity, medicated, onMedicatedChange, readOnly }) {
  const styles = useStyles();
  const [{ isProducer }] = useStateValue();
  const ownerId = bin?.getSubEntity('https://api.binsentry.com/rel/bin-owner')?.href;
  const { feedOptions, loading: loadingFeedOptions } = useFeedOptions({ organizationId: ownerId });

  if (loadingFeedOptions) {
    return <LinearProgress />;
  }

  if (!feedOptions) {
    return null;
  }

  const selectedFeed = _.find(feedOptions, (f) => f.feedId === defaultFeedId) || null;

  return (
    <form className={styles.form} dir='ltr'>
      <BnAutocompleteField
        disableClearable={true}
        label='Feed Type'
        variant='outlined'
        optionLabel='name'
        options={feedOptions}
        value={selectedFeed}
        onChange={(value) => {
          onFeedChange(value?.feedId);
        }}
      />
      {!isProducer &&
        // The medicated field is not present on actions when the user is a producer
        // However, the current order form is completely decoupled from the action, so there
        // is no way to verify if the field is present or not until the user submits
        // Checking if the user isProducer is a temporary solution until the order form is
        // phased out in favor of the newer multi-bin order form
        <BnToggleSwitch
          label='Medicated'
          checked={medicated}
          disabled={readOnly}
          onClick={(e) => onMedicatedChange(e.target.checked)}
        />
      }
      <div className={styles.amountContainer}>
        <TextField
          label={
            <span>
              Order Amount<span className={styles.legend}/>
            </span>
          }
          variant='outlined'
          className={readOnly ? styles.readOnlyTextField : null}
          type='number'
          onChange={(event) => setOrderQuantity(event.target.value)}
          value={orderQuantity}
          error={!readOnly && (orderQuantity > maxBinLevel || orderQuantity < 0)}
          helperText={!readOnly && orderQuantity > maxBinLevel && 'Overfill predicted'}
          InputProps={{
            readOnly,
            endAdornment: <InputAdornment position='end'>{orderUnit}</InputAdornment>,
            inputProps: {
              // yes, Material actually nests input props like this, and yes, it's case sensitive
              step: '0.5',
              min: '0',
            },
          }}
          InputLabelProps={{ variant: 'outlined' }}
        />
        <div className={styles.maxRecommendedContainer}>
          <span className={styles.maxRecommendedLabel}>
            Max Recommended:
          </span>
          <Button disabled={readOnly || !selectedFeed} className={styles.setMaxButton} onClick={() => setOrderQuantity(parseFloat(maxBinLevel.toFixed(2)))}>
            {selectedFeed ? (
              <span className={styles.selectFeedText}>
                {parseFloat(maxBinLevel.toFixed(2))} {orderUnit}
              </span>
            ) : (
              <span className={styles.selectFeedWarning}>Select Feed</span>
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
